import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { RefreshCw, CheckCircle, AlertCircle, Radio, Zap, ExternalLink, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DIVISION_ORDER = ["Inside NSB", "High School", "BC9 HBCU", "D1", "D2", "D3", "NAIA", "JUCO"];

export function TwinSyncManager() {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [triggeringAll, setTriggeringAll] = useState(false);
  const [triggeringDiv, setTriggeringDiv] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [triggerResult, setTriggerResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem("token") || "";

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/twin/status`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) setStatus(await res.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/twin/sync`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
      });
      const data = await res.json();
      setSyncResult(data);
      const totalNew = Object.values(data.results || {}).reduce((sum, r) => sum + (r.new_articles || 0), 0);
      if (totalNew > 0) {
        toast.success(`Synced ${totalNew} new article${totalNew > 1 ? "s" : ""} from Twin.so`);
      } else {
        toast.info("No new articles found. Agents may not have completed runs yet.");
      }
      fetchStatus();
    } catch (err) {
      setSyncResult({ error: err.message });
      toast.error("Sync failed: " + err.message);
    }
    setSyncing(false);
  };

  const handleTriggerAll = async () => {
    setTriggeringAll(true);
    setTriggerResult(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/twin/trigger-all`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
      });
      const data = await res.json();
      setTriggerResult(data);
      const triggered = Object.values(data.results || {}).filter(r => r.status === "triggered").length;
      const errors = Object.values(data.results || {}).filter(r => r.status === "error").length;
      if (triggered > 0) {
        toast.success(`Triggered ${triggered} agent${triggered > 1 ? "s" : ""}${errors > 0 ? ` (${errors} failed)` : ""}. Wait a few minutes then click "Sync All Agents".`);
      } else {
        toast.error("Failed to trigger agents.");
      }
    } catch (err) {
      setTriggerResult({ error: err.message });
      toast.error("Trigger failed: " + err.message);
    }
    setTriggeringAll(false);
  };

  const handleTriggerDivision = async (division) => {
    setTriggeringDiv(division);
    try {
      const res = await fetch(`${API_URL}/api/admin/twin/trigger/${encodeURIComponent(division)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" },
      });
      if (res.ok) {
        toast.success(`${division} agent triggered. Wait a few minutes then sync.`);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Failed to trigger ${division} agent`);
      }
    } catch (err) {
      toast.error(`Trigger failed: ${err.message}`);
    }
    setTriggeringDiv(null);
  };

  if (loading) return <div className="text-center py-8 text-white/40">Loading Twin.so status...</div>;

  return (
    <div className="space-y-6" data-testid="twin-sync-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Radio className="w-5 h-5 text-red-400" />
            Twin.so ScoutWire Sync
          </h3>
          <p className="text-sm text-white/40 mt-1">Trigger agents to gather news, then sync articles into the ScoutWire feed.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={handleTriggerAll}
            disabled={triggeringAll || !status?.configured}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="twin-trigger-all-btn"
          >
            {triggeringAll ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
            {triggeringAll ? "Triggering..." : "Trigger All Agents"}
          </Button>
          <Button
            onClick={handleSync}
            disabled={syncing || !status?.configured}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="twin-sync-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? "Syncing..." : "Sync All Agents"}
          </Button>
        </div>
      </div>

      {/* Workflow Hint */}
      <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
        <p className="text-xs text-amber-300/80">
          <span className="font-bold text-amber-300">Workflow:</span> Click "Trigger All Agents" to start agent runs on Twin.so.
          Wait 2-5 minutes for agents to finish, then click "Sync All Agents" to pull new articles into the News Desk.
        </p>
      </div>

      {/* API Key Status */}
      <div className={`rounded-lg border p-4 ${status?.configured ? 'border-green-500/30 bg-green-500/10' : 'border-red-500/30 bg-red-500/10'}`}>
        <div className="flex items-center gap-2">
          {status?.configured ? (
            <><CheckCircle className="w-4 h-4 text-green-400" /><span className="text-sm text-green-400 font-medium">Twin.so API Key Configured</span></>
          ) : (
            <><AlertCircle className="w-4 h-4 text-red-400" /><span className="text-sm text-red-400 font-medium">Twin.so API Key Not Set — Add TWIN_API_KEY to backend .env</span></>
          )}
        </div>
      </div>

      {/* Division Agents Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {DIVISION_ORDER.filter(d => d !== "Inside NSB").map((div) => {
          const info = status?.divisions?.[div] || {};
          const syncDiv = syncResult?.results?.[div];
          const trigDiv = triggerResult?.results?.[div];
          const isTriggering = triggeringDiv === div;
          return (
            <div key={div} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`twin-div-${div.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-white">{div}</span>
                <div className="flex items-center gap-1">
                  {syncDiv?.status === "ok" && syncDiv?.new_articles > 0 && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-bold">+{syncDiv.new_articles}</span>
                  )}
                  {trigDiv?.status === "triggered" && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-bold">Running</span>
                  )}
                </div>
              </div>
              <div className="text-xs text-white/40 space-y-1">
                <div className="flex justify-between">
                  <span>Total articles</span>
                  <span className="text-white/70">{info.total_articles || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>From Twin.so</span>
                  <span className="text-white/70">{info.twin_articles || 0}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleTriggerDivision(div)}
                  disabled={isTriggering || !status?.configured}
                  className="flex-1 text-[10px] h-7 border-white/10 text-white/60 hover:text-white hover:bg-white/10"
                  data-testid={`trigger-${div.toLowerCase().replace(/\s/g,'-')}`}
                >
                  {isTriggering ? <Loader2 size={10} className="animate-spin mr-1" /> : <Zap size={10} className="mr-1" />}
                  Trigger
                </Button>
                {info.agent_id && (
                  <a
                    href={`https://builder.twin.so/agent/${info.agent_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[10px] text-red-400/60 hover:text-red-400 transition px-2 py-1"
                  >
                    <ExternalLink size={10} />
                  </a>
                )}
              </div>
              {syncDiv?.status === "error" && (
                <div className="mt-2 text-[10px] text-red-400 truncate">{syncDiv.error}</div>
              )}
              {trigDiv?.status === "error" && (
                <div className="mt-2 text-[10px] text-red-400 truncate">{trigDiv.error}</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Trigger Result */}
      {triggerResult && !triggerResult.error && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-4">
          <h4 className="text-sm font-bold text-amber-300 mb-2">Trigger Results</h4>
          <div className="space-y-1">
            {Object.entries(triggerResult.results || {}).map(([div, r]) => (
              <div key={div} className="flex items-center justify-between text-xs">
                <span className="text-white/60">{div}</span>
                <span className={r.status === "triggered" ? "text-amber-400" : "text-red-400"}>
                  {r.status === "triggered" ? "Agent running" : r.error}
                </span>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-amber-300/50 mt-2">Agents take 2-5 minutes to complete. Click "Sync All Agents" after they finish.</p>
        </div>
      )}

      {/* Sync Result */}
      {syncResult && (
        <div className="rounded-lg border border-white/10 bg-white/5 p-4">
          <h4 className="text-sm font-bold text-white mb-2">Sync Results</h4>
          {syncResult.error ? (
            <p className="text-sm text-red-400">{syncResult.error}</p>
          ) : (
            <div className="space-y-1">
              {Object.entries(syncResult.results || {}).map(([div, r]) => (
                <div key={div} className="flex items-center justify-between text-xs">
                  <span className="text-white/60">{div}</span>
                  <span className={r.status === "ok" ? "text-green-400" : "text-red-400"}>
                    {r.status === "ok" ? `${r.new_articles} new articles` : r.error}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
